import type { DisciplineDetails } from './interfaces'

import { useListsStore } from '@shared/model'

import { useDisciplineStore } from './store'
import { useDisciplineGeneralInfoStore } from './general-info/store'
import { useDisciplinePrivileges } from './discipline-privileges.hook'
import { buildDisciplineForm, getDisciplineName } from './discipline-general-info'

interface DisciplineGeneralReturn {
  refDetails: ComputedRef<DisciplineDetails | null>
  refTitle: ComputedRef<string>
  refIsCustomerGo: ComputedRef<boolean>
  canViewDisciplines: ComputedRef<boolean>
}

/** Section-level state of the General info tab. Called once per page: it owns the form reset that
 *  follows every fresh read of the discipline. */
export const useDisciplineGeneral = (): DisciplineGeneralReturn => {
  const disciplineStore = useDisciplineStore()
  const generalInfoStore = useDisciplineGeneralInfoStore()
  const listsStore = useListsStore()
  const { canViewDisciplines } = useDisciplinePrivileges()

  const refDetails = computed<DisciplineDetails | null>(() => disciplineStore.getDetails)

  const refTitle = computed<string>(() => {
    const discipline = disciplineStore.getDiscipline
    const kidName = refDetails.value?.kid.name ?? discipline?.kid.name ?? ''
    const code = refDetails.value?.code ?? discipline?.code ?? ''
    const name = getDisciplineName(code, listsStore.getLists?.discipline.code)

    return `Kid / ${kidName} / ${name} program`
  })

  const refIsCustomerGo = computed<boolean>(() => disciplineStore.isCustomerGo)

  watch(
    refDetails,
    (details) => {
      generalInfoStore.setForm(details ? buildDisciplineForm(details) : null)
    },
    { immediate: true },
  )

  return {
    refDetails,
    refTitle,
    refIsCustomerGo,
    canViewDisciplines,
  }
}
